import { useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CategoryField } from 'categories/components';
import { Form, FormField } from 'core/components';
import { InputType, NotificationPosition } from 'core/enums';
import { DiscountApplyToTypes } from 'corporate/enums';
import {
  CompanyPartSpecificationField,
  ManufacturerField,
  PartSpecificationField,
} from 'pricing/components';
import { IPricingProfileCostingCalculation } from 'pricing/types';

import { RemovePricingProfileCostingCalculationsButton } from './RemovePricingProfileCostingCalculationsButton';
import { IPricingProfileCostingCalculationForm } from './types';

interface IProps {
  pricingProfileCostingCalculation?: IPricingProfileCostingCalculation;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    reset?: UseFormReset<IPricingProfileCostingCalculationForm>
  ) => (data: IPricingProfileCostingCalculationForm) => Promise<void>;
  onRemoveSuccess?: () => void;
}

export const UpsertPricingProfileCostingCalculationFormFields = ({
  pricingProfileCostingCalculation,
  isOpen,
  onClose,
  onSubmit,
  onRemoveSuccess,
}: IProps) => {
  const { t } = useTranslation();

  const title = pricingProfileCostingCalculation
    ? t('titleUpdateCostingCalculation')
    : t('titleAddCostingCalculation');

  const removeButton = useMemo(() => {
    return pricingProfileCostingCalculation ? (
      <RemovePricingProfileCostingCalculationsButton
        pricingProfileCostingCalculations={[pricingProfileCostingCalculation]}
        buttonLabel={t('buttonLabelRemovePricingProfileCostingCalculation')}
        onRemoveSuccess={onRemoveSuccess}
        notificationPosition={NotificationPosition.FORM}
      />
    ) : null;
  }, [pricingProfileCostingCalculation, onRemoveSuccess, t]);

  const applyToChoices = [
    { value: DiscountApplyToTypes.STD_COST, label: t('labelStandardCost') },
    { value: DiscountApplyToTypes.LIST_PRICE, label: t('labelListPrice') },
  ];

  return (
    <Form
      title={title}
      isOpen={isOpen}
      onClose={onClose}
      onSubmit={onSubmit}
      extraButtons={removeButton}
    >
      <ManufacturerField name="manufacturer" />

      <CategoryField name="category" label={t('labelCategory')} placeholder={t('labelAll')} />

      <PartSpecificationField name="partFieldCriteria" />

      <CompanyPartSpecificationField name="companyPartFieldCriteria" />

      <FormField
        name="applyTo"
        type={InputType.RADIO_GROUP}
        inputProps={{
          label: t('labelApplyDiscountTo'),
          required: true,
          choices: applyToChoices,
        }}
      />

      <FormField
        name="value"
        type={InputType.NUMBER_INPUT}
        inputProps={{
          label: t('labelCostDiscount'),
          required: true,
          endDecorator: '%',
        }}
      />
    </Form>
  );
};
